import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Focus, Settings, Volume2, VolumeX, Bell, BellOff, Wifi, WifiOff, Pause, AlertCircle, Activity, X, Archive, Archive as ArchiveIcon } from 'lucide-react';

interface FocusSession {
  id: string;
  startTime: Date;
  endTime?: Date;
  duration: number; // in seconds
  goal: number; // in minutes
  isActive: boolean;
  distractions: number;
  breaks: number;
  productivity: number; // 0-100
  environment: {
    soundEnabled: boolean;
    notificationsEnabled: boolean;
    internetEnabled: boolean;
    theme: 'light' | 'dark' | 'auto';
    fontSize: 'small' | 'medium' | 'large';
    lineHeight: 'tight' | 'normal' | 'loose';
    fontFamily: 'serif' | 'sans-serif' | 'monospace';
  };
}

interface FocusModeProps {
  onSessionStart?: (session: FocusSession) => void;
  onSessionEnd?: (session: FocusSession) => void;
  onSessionUpdate?: (session: FocusSession) => void;
  className?: string;
}

const FocusMode: React.FC<FocusModeProps> = ({
  onSessionStart,
  onSessionEnd,
  onSessionUpdate,
  className = ''
}) => {
  const [isActive, setIsActive] = useState(false);
  const [currentSession, setCurrentSession] = useState<FocusSession | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [sessions, setSessions] = useState<FocusSession[]>([]);
  const [timer, setTimer] = useState(0);
  const [goal, setGoal] = useState(25); // 25 minutes default
  const [distractions, setDistractions] = useState(0);
  const [breaks, setBreaks] = useState(0);
  const [productivity, setProductivity] = useState(100);
  const [showBreakReminder, setShowBreakReminder] = useState(false);
  const [breakTime, setBreakTime] = useState(5); // 5 minutes break
  const [isBreak, setIsBreak] = useState(false);
  const [breakTimer, setBreakTimer] = useState(0);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const breakTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Environment settings
  const [environment, setEnvironment] = useState({
    soundEnabled: true,
    notificationsEnabled: false,
    internetEnabled: true,
    theme: 'dark' as 'light' | 'dark' | 'auto',
    fontSize: 'medium' as 'small' | 'medium' | 'large',
    lineHeight: 'normal' as 'tight' | 'normal' | 'loose',
    fontFamily: 'serif' as 'serif' | 'sans-serif' | 'monospace'
  });

  // Start focus session
  const startSession = useCallback(() => {
    const session: FocusSession = {
      id: Date.now().toString(),
      startTime: new Date(),
      duration: 0,
      goal: goal * 60, // convert to seconds
      isActive: true,
      distractions: 0,
      breaks: 0,
      productivity: 100,
      environment
    };

    setCurrentSession(session);
    setIsActive(true);
    setTimer(0);
    setDistractions(0);
    setBreaks(0);
    setProductivity(100);
    onSessionStart?.(session);

    // Start timer
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        const newTime = prev + 1;
        const session = { ...currentSession!, duration: newTime };
        setCurrentSession(session);
        onSessionUpdate?.(session);
        return newTime;
      });
    }, 1000);
  }, [goal, environment, currentSession, onSessionStart, onSessionUpdate]);

  // End focus session
  const endSession = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (currentSession) {
      const completedSession = {
        ...currentSession,
        endTime: new Date(),
        duration: timer,
        isActive: false,
        distractions,
        breaks,
        productivity: Math.max(0, productivity - (distractions * 5) - (breaks * 2))
      };

      setSessions(prev => [...prev, completedSession]);
      onSessionEnd?.(completedSession);
    }

    setCurrentSession(null);
    setIsActive(false);
    setTimer(0);
    setDistractions(0);
    setBreaks(0);
    setProductivity(100);
  }, [currentSession, timer, distractions, breaks, productivity, onSessionEnd]);

  // Take a break
  const takeBreak = useCallback(() => {
    if (isActive) {
      setBreaks(prev => prev + 1);
      setIsBreak(true);
      setBreakTimer(breakTime * 60);
      
      breakTimerRef.current = setInterval(() => {
        setBreakTimer(prev => {
          if (prev <= 1) {
            setIsBreak(false);
            if (breakTimerRef.current) {
              clearInterval(breakTimerRef.current);
              breakTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
  }, [isActive, breakTime]);

  // Record distraction
  const recordDistraction = useCallback(() => {
    if (isActive) {
      setDistractions(prev => prev + 1);
      setProductivity(prev => Math.max(0, prev - 5));
    }
  }, [isActive]);

  // Format time
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Check if goal is reached
  useEffect(() => {
    if (isActive && timer >= goal * 60) {
      setShowBreakReminder(true);
    }
  }, [isActive, timer, goal]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (breakTimerRef.current) {
        clearInterval(breakTimerRef.current);
      }
    };
  }, []);

  // Apply environment settings
  useEffect(() => {
    if (isActive) {
      // Apply theme
      document.documentElement.classList.toggle('dark', environment.theme === 'dark');
      
      // Apply font settings
      const content = document.querySelector('.prose') as HTMLElement;
      if (content) {
        content.style.fontSize = environment.fontSize === 'small' ? '14px' : 
                                environment.fontSize === 'large' ? '18px' : '16px';
        content.style.lineHeight = environment.lineHeight === 'tight' ? '1.4' : 
                                  environment.lineHeight === 'loose' ? '1.8' : '1.6';
        content.style.fontFamily = environment.fontFamily === 'serif' ? 'serif' : 
                                  environment.fontFamily === 'monospace' ? 'monospace' : 'sans-serif';
      }
    }
  }, [isActive, environment]);

  const progress = isActive ? (timer / (goal * 60)) * 100 : 0;
  const breakProgress = isBreak ? ((breakTime * 60 - breakTimer) / (breakTime * 60)) * 100 : 0;

  return (
    <div className={`focus-mode ${className}`}>
      {/* Focus Mode Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-3">
            <button
              onClick={isActive ? endSession : startSession}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isActive 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              <Focus className="w-4 h-4" />
              {isActive ? 'End Focus' : 'Start Focus'}
            </button>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Settings className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => setShowStats(!showStats)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Activity className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Active Session Display */}
      {isActive && (
        <div className="fixed bottom-4 left-4 right-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-gray-900 dark:text-white">Focus Mode Active</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {formatTime(timer)} / {goal}:00
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={recordDistraction}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-red-500"
                  title="Record Distraction"
                >
                  <AlertCircle className="w-4 h-4" />
                </button>
                
                <button
                  onClick={takeBreak}
                  disabled={isBreak}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  title="Take Break"
                >
                  <Pause className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-2">
              <div 
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>Distractions: {distractions}</span>
              <span>Breaks: {breaks}</span>
              <span>Productivity: {productivity}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Break Mode */}
      {isBreak && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <Pause className="w-8 h-8 text-blue-500" />
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Break Time
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Take a {breakTime}-minute break to recharge
              </p>
              
              <div className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {formatTime(breakTimer)}
              </div>
              
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-6">
                <div 
                  className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${breakProgress}%` }}
                />
              </div>
              
              <button
                onClick={() => {
                  setIsBreak(false);
                  if (breakTimerRef.current) {
                    clearInterval(breakTimerRef.current);
                    breakTimerRef.current = null;
                  }
                  setBreakTimer(0);
                }}
                className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                End Break
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Focus Settings
              </h3>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Goal Setting */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Focus Goal (minutes)
                </label>
                <input
                  type="number"
                  value={goal}
                  onChange={(e) => setGoal(parseInt(e.target.value) || 25)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min="1"
                  max="120"
                />
              </div>
              
              {/* Break Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Break Time (minutes)
                </label>
                <input
                  type="number"
                  value={breakTime}
                  onChange={(e) => setBreakTime(parseInt(e.target.value) || 5)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  min="1"
                  max="30"
                />
              </div>
              
              {/* Environment Settings */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-white">Environment</h4>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Sound</span>
                  <button
                    onClick={() => setEnvironment(prev => ({ ...prev, soundEnabled: !prev.soundEnabled }))}
                    className={`p-2 rounded-lg transition-colors ${
                      environment.soundEnabled 
                        ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {environment.soundEnabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Notifications</span>
                  <button
                    onClick={() => setEnvironment(prev => ({ ...prev, notificationsEnabled: !prev.notificationsEnabled }))}
                    className={`p-2 rounded-lg transition-colors ${
                      environment.notificationsEnabled 
                        ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {environment.notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-700 dark:text-gray-300">Internet</span>
                  <button
                    onClick={() => setEnvironment(prev => ({ ...prev, internetEnabled: !prev.internetEnabled }))}
                    className={`p-2 rounded-lg transition-colors ${
                      environment.internetEnabled 
                        ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}
                  >
                    {environment.internetEnabled ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              {/* Theme Settings */}
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900 dark:text-white">Reading Experience</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Theme
                  </label>
                  <select
                    value={environment.theme}
                    onChange={(e) => setEnvironment(prev => ({ ...prev, theme: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Font Size
                  </label>
                  <select
                    value={environment.fontSize}
                    onChange={(e) => setEnvironment(prev => ({ ...prev, fontSize: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Font Family
                  </label>
                  <select
                    value={environment.fontFamily}
                    onChange={(e) => setEnvironment(prev => ({ ...prev, fontFamily: e.target.value as any }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="serif">Serif</option>
                    <option value="sans-serif">Sans Serif</option>
                    <option value="monospace">Monospace</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStats && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-6 max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Focus Statistics
              </h3>
              <button
                onClick={() => setShowStats(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {sessions.length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Sessions</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.round(sessions.reduce((sum, session) => sum + session.duration, 0) / 60)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Total Minutes</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {Math.round(sessions.reduce((sum, session) => sum + session.productivity, 0) / sessions.length) || 0}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Avg Productivity</div>
              </div>
              
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {sessions.reduce((sum, session) => sum + session.distractions, 0)}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Distractions</div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900 dark:text-white">Recent Sessions</h4>
              {sessions.slice(-5).map(session => (
                <div key={session.id} className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatTime(session.duration)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {session.startTime.toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                    <span>Productivity: {session.productivity}%</span>
                    <span>Distractions: {session.distractions}</span>
                    <span>Breaks: {session.breaks}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FocusMode;
